<template>
  <div>
    <AppTitle>
      <template #title>เครื่องกระตุกหัวใจ (Defibrillator)</template>
      <template #subtitle>Wanon Niwat Hospital [5S SYSTEM WNH]</template>
      <template #breadcrumb>หน้าหลัก</template>
    </AppTitle>

    <div class="row">
      <div class="col-md-12">
        <div class="tile">
          <div
            class="tile-title-w-btn d-flex justify-content-between align-items-center"
          >
            <h3 class="title">เครื่องกระตุกหัวใจทั้งหมด</h3>

            <div class="d-flex align-items-center gap-2">
              <select
                v-model="filterKey"
                class="form-select"
                style="width: auto"
              >
                <option value="code">Code</option>
                <option value="name">Name</option>
                <option value="group.name">Department</option>
              </select>

              <input
                v-if="filterKey !== 'group.name'"
                type="text"
                v-model="filterText"
                placeholder="Search..."
                class="form-control"
                style="width: 200px"
              />

              <select
                v-else
                class="form-select"
                v-model="selectedGroupId"
                style="width: 200px"
              >
                <option value="">All Departments</option>
                <option
                  v-for="group in groups"
                  :key="group.id"
                  :value="group.id"
                >
                  {{ group.name }}
                </option>
              </select>

              <button class="btn btn-primary icon-btn" @click="openAddModal">
                <i class="bi bi-plus-square me-2"></i> เพิ่มรายการ
              </button>
            </div>
          </div>

          <Overlay :showOverlay="isLoading" />

          <div class="tile-body">
            <div class="table-responsive">
              <table class="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in filteredItems" :key="item.id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.code }}</td>
                    <td>{{ item.name }}</td>
                    <td>{{ item.group?.name }}</td>
                    <td>
                      <div class="btn-group btn-group-sm">
                        <button
                          class="btn btn-warning"
                          @click="openEditModal(item)"
                        >
                          <i class="bi bi-pencil"></i> Edit
                        </button>
                        <button
                          class="btn btn-danger"
                          @click="handleDelete(item.id)"
                        >
                          <i class="bi bi-trash"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Modal -->
          <div
            class="modal fade"
            id="defibModal"
            tabindex="-1"
            aria-labelledby="modalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title" id="modalLabel">
                    {{ modalMode === "edit" ? "แก้ไขข้อมูล" : "เพิ่มรายการ" }}
                  </h5>
                  <button
                    type="button"
                    class="btn-close"
                    @click="hideModal"
                  ></button>
                </div>

                <form @submit.prevent="handleSubmit">
                  <div class="modal-body">
                    <div v-if="modalMode !== 'add'" class="text-center mb-3">
                      <button
                        class="btn btn-success"
                        @click.prevent="downloadQRCode"
                      >
                        Download QR Code
                      </button>
                    </div>

                    <div
                      v-if="editingItem.code"
                      ref="qrCodeContainer"
                      class="text-center p-4"
                    >
                      <qrcode-vue
                        :value="`${url}/${editingItem.code}`"
                        size="128"
                      ></qrcode-vue>
                      <p class="mt-2 mb-0">
                        {{ editingItem.name }} [{{ editingItem.code }}]
                      </p>
                      <p class="mb-0">{{ editingItem.group?.name }}</p>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">ชื่ออุปกรณ์</label>
                      <input
                        type="text"
                        v-model="editingItem.name"
                        class="form-control"
                        required
                      />
                    </div>

                    <div class="mb-3">
                      <label class="form-label">หน่วยงาน</label>
                      <select
                        v-model="editingItem.group_id"
                        class="form-select"
                        required
                      >
                        <option value="" disabled>- เลือก -</option>
                        <option
                          v-for="dept in groups"
                          :key="dept.id"
                          :value="dept.id"
                        >
                          {{ dept.name }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="modal-footer">
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="hideModal"
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary"
                      :disabled="isSubmitting"
                    >
                      <span v-if="isSubmitting">
                        <span
                          class="spinner-border spinner-border-sm me-1"
                        ></span>
                        Saving...
                      </span>
                      <span v-else>Save changes</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axiosInstance from "@/utils/axiosInstance";
import {
  showConfirm,
  showCreateAlert,
  showUpdateAlert,
  showDeleteAlert,
  showErrorAlert,
} from "@/utils/sweetalert2-utils";
import Overlay from "@/components/Overlay.vue";
import AppTitle from "@/layouts/AppTitle.vue";
import QrcodeVue from "qrcode.vue";
import html2canvas from "html2canvas";
import { Modal } from "bootstrap";

// State
const url = ref(`${process.env.VUE_APP_URL_BASE}/view-defibrillator`);
const defibrillators = ref([]);
const groups = ref([]);
const filterKey = ref("code");
const filterText = ref("");
const selectedGroupId = ref("");
const isLoading = ref(true);
const isSubmitting = ref(false);
const modalMode = ref("add");
const editingItem = ref({ id: null, name: "", code: "", group_id: "" });
const qrCodeContainer = ref(null);

// Fetch
const fetchData = async () => {
  isLoading.value = true;
  try {
    const [defibRes, groupRes] = await Promise.all([
      axiosInstance.get("/defibrillator"),
      axiosInstance.get("/group"),
    ]);
    defibrillators.value = defibRes.data;
    groups.value = groupRes.data;
  } catch (err) {
    showErrorAlert("ไม่สามารถโหลดข้อมูลได้");
  } finally {
    isLoading.value = false;
  }
};

// Filter
const filteredItems = computed(() => {
  return defibrillators.value.filter((item) => {
    if (filterKey.value === "group.name") {
      return selectedGroupId.value
        ? item.group_id === selectedGroupId.value
        : true;
    } else {
      const value = item[filterKey.value] || "";
      return value.toLowerCase().includes(filterText.value.toLowerCase());
    }
  });
});

// Modal control
const showModal = () => {
  new Modal(document.getElementById("defibModal")).show();
};

const hideModal = () => {
  const modalEl = document.getElementById("defibModal");
  Modal.getInstance(modalEl).hide();
};

// Actions
const openAddModal = () => {
  modalMode.value = "add";
  editingItem.value = {
    id: null,
    name: "Defibrillator",
    code: "",
    group_id: "",
  };
  showModal();
};

const openEditModal = (item) => {
  modalMode.value = "edit";
  editingItem.value = { ...item };
  showModal();
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    if (modalMode.value === "add") {
      await axiosInstance.post("/defibrillator", editingItem.value);
      showCreateAlert();
    } else {
      await axiosInstance.put(
        `/defibrillator/${editingItem.value.id}`,
        editingItem.value
      );
      showUpdateAlert();
    }
    hideModal();
    fetchData();
  } catch (err) {
    showErrorAlert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
  } finally {
    isSubmitting.value = false;
  }
};

const handleDelete = async (id) => {
  const result = await showConfirm();
  if (result.isConfirmed) {
    try {
      await axiosInstance.delete(`/defibrillator/${id}`);
      defibrillators.value = defibrillators.value.filter(
        (item) => item.id !== id
      );
      showDeleteAlert();
    } catch (err) {
      showErrorAlert("ลบไม่สำเร็จ");
    }
  }
};

const downloadQRCode = () => {
  if (!qrCodeContainer.value) return;
  html2canvas(qrCodeContainer.value).then((canvas) => {
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `${editingItem.value.code} ${editingItem.value.group?.name}.png`;
    link.click();
  });
};

onMounted(fetchData);
</script>
